import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MessageCircle, Send, User, Mail, Phone, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

const Scheduling = () => {
  const [activeTab, setActiveTab] = useState('contact-form');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const phoneNumber = '5555984328501'; // WhatsApp number

  const handleGoogleCalendarClick = () => {
    // Instead of opening Google Calendar, open WhatsApp with a scheduling message.
    handleWhatsAppClick('Olá! Gostaria de agendar uma consultoria pela TALC Assessoria Contábil.');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Erro no envio');
      const json = await res.json();
      toast({
        title: 'Mensagem enviada',
        description: 'Sua mensagem foi enviada com sucesso. Entraremos em contato em breve.',
      });
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (err) {
      console.error(err);
      toast({
        variant: 'destructive',
        title: 'Erro ao enviar',
        description: 'Não foi possível enviar a mensagem. Tente novamente mais tarde.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppClick = (message) => {
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="scheduling" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#c99b36] font-semibold text-lg mb-2 block">Contato e Agendamento</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a4b3c] mb-4">
            Fale Conosco
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Escolha a melhor forma para entrar em contato com a nossa equipe
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 mb-8 bg-[#e5e5e5] p-2 rounded-xl">
            <button
              onClick={() => setActiveTab('contact-form')}
              className={`flex-1 py-4 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'contact-form'
                  ? 'bg-[#0a4b3c] text-white shadow-lg'
                  : 'bg-transparent text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Send size={20} />
              <span>Formulário de Contato</span>
            </button>
            <button
              onClick={() => handleWhatsAppClick('Olá! Gostaria de falar com a TALC Assessoria Contábil.')}
              className={`flex-1 py-4 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'whatsapp'
                  ? 'bg-[#0a4b3c] text-white shadow-lg'
                  : 'bg-transparent text-gray-700 hover:bg-gray-200'
              }`}
            >
              <MessageCircle size={20} />
              <span>Atendimento via WhatsApp</span>
            </button>
            {/* Google Agenda button removido */}
          </div>

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white border border-[#e5e5e5] rounded-2xl p-8 shadow-xl"
          >
            {activeTab === 'contact-form' && (
              <div>
                <div className="w-20 h-20 bg-[#0a4b3c]/10 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <Send size={40} className="text-[#0a4b3c]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0a4b3c] mb-4 text-center">Envie sua Mensagem por E-mail</h3>
                <p className="text-gray-600 mb-6 text-center">O formulário abaixo abrirá seu cliente de e-mail para enviar para admtalc@gmail.com</p>

                <form onSubmit={handleEmailSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                        <User size={18} />
                        Nome Completo
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#0a4b3c] focus:outline-none transition-colors duration-200"
                        placeholder="Seu nome"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                        <Mail size={18} />
                        E-mail
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#0a4b3c] focus:outline-none transition-colors duration-200"
                        placeholder="seu@email.com"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                        <Phone size={18} />
                        Telefone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#0a4b3c] focus:outline-none transition-colors duration-200"
                        placeholder="(00) 00000-0000"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                        <Building size={18} />
                        Empresa (Opcional)
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#0a4b3c] focus:outline-none transition-colors duration-200"
                        placeholder="Nome da empresa"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                      <Send size={18} />
                      Mensagem
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-[#0a4b3c] focus:outline-none transition-colors duration-200 resize-none"
                      placeholder="Descreva o que você precisa ou como podemos ajudar sua empresa..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#0a4b3c] hover:bg-[#063127] text-white py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </div>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Scheduling;